/**
 * UTILIDADES DE SANITIZACIÓN NUMÉRICA
 * Previene inyección de caracteres no numéricos y valida límites lógicos
 */
import { toDateSafe, toISOStringSafe, toLocaleDateStringSafe, diferenciaAnosSafe } from './fechasSeguras'

/**
 * Sanitiza un valor numérico (moneda)
 * @param {any} valor - Valor a sanitizar
 * @param {number} [min=0] - Valor mínimo permitido
 * @param {number} [max=100000000] - Valor máximo permitido (100 millones)
 * @param {number} [defaultValue=0] - Valor por defecto si es inválido
 * @returns {number} Valor sanitizado
 */
export function sanitizarMoneda(valor, min = 0, max = 100000000, defaultValue = 0) {
  if (valor === null || valor === undefined || valor === '') return defaultValue
  const num = parseMonedaLocale(valor, defaultValue)
  
  if (isNaN(num)) return defaultValue
  if (num < min) return min
  if (num > max) return max
  return num
}

/**
 * Parsea montos en formato local (es-AR / en-US)
 * Ejemplos: "29.876,30", "29,876.30", "29876,30", "29876.30"
 * @param {any} valor
 * @param {number} [defaultValue=0]
 * @returns {number}
 */
export function parseMonedaLocale(valor, defaultValue = 0) {
  if (typeof valor === 'number') {
    return Number.isFinite(valor) ? valor : defaultValue
  }
  const raw = String(valor ?? '').trim()
  if (!raw) return defaultValue
  const cleaned = raw
    .replace(/\$/g, '')
    .replace(/\s+/g, '')
    .replace(/[^\d,.-]/g, '')
  if (!cleaned) return defaultValue

  const hasDot = cleaned.includes('.')
  const hasComma = cleaned.includes(',')
  let normalized = cleaned
  if (hasDot && hasComma) {
    const lastDot = cleaned.lastIndexOf('.')
    const lastComma = cleaned.lastIndexOf(',')
    const decimalSep = lastDot > lastComma ? '.' : ','
    const thousandSep = decimalSep === '.' ? ',' : '.'
    normalized = cleaned.split(thousandSep).join('').replace(decimalSep, '.')
  } else if (hasComma && !hasDot) {
    normalized = cleaned.replace(/\./g, '').replace(',', '.')
  } else {
    normalized = cleaned.replace(/,/g, '')
  }

  const n = Number(normalized)
  return Number.isFinite(n) ? n : defaultValue
}

/**
 * Sanitiza un valor de años
 * @param {any} valor - Valor a sanitizar
 * @param {number} [min=0] - Mínimo (0 años)
 * @param {number} [max=60] - Máximo (60 años, límite lógico)
 * @param {number} [defaultValue=0] - Valor por defecto
 * @returns {number} Años sanitizados
 */
export function sanitizarAños(valor, min = 0, max = 60, defaultValue = 0) {
  if (valor === null || valor === undefined || valor === '') return defaultValue
  
  const num = parseInt(String(valor), 10)
  
  if (isNaN(num)) return defaultValue
  if (num < min) return min
  if (num > max) return max
  return num
}

/**
 * Sanitiza la cantidad de hijos
 * @param {any} valor - Valor a sanitizar
 * @returns {number} Cantidad entre 0 y 50
 */
export function sanitizarHijos(valor) {
  return sanitizarAños(valor, 0, 50, 0)
}

/**
 * Valida que una fecha sea coherente (desde < hasta)
 * @param {any} desde - Fecha inicial
 * @param {any} hasta - Fecha final
 * @param {Date} [hoy=new Date()] - Fecha actual para comparar
 * @returns {Object} {valido, error}
 */
export function validarCoherenciaTemporal(desde, hasta, hoy = new Date()) {
  const d1 = toDateSafe(desde)
  const d2 = toDateSafe(hasta)
  
  if (!d1 || !d2) return { valido: false, error: 'Fechas inválidas' }
  if (d1 > d2) return { valido: false, error: 'La fecha "Desde" debe ser anterior a "Hasta"' }
  if (d2 > hoy) return { valido: false, error: 'La fecha "Hasta" no puede ser futura' }
  
  return { valido: true, error: null }
}

// Re-exportar para conveniencia
export { toDateSafe, toISOStringSafe, toLocaleDateStringSafe, diferenciaAnosSafe } from './fechasSeguras'

/**
 * Verifica si un usuario tiene saldo suficiente de coins
 * @param {number} saldo - Saldo actual
 * @param {number} costo - Costo del cálculo
 * @returns {Object} {suficiente, mensaje}
 */
export function validarSaldoCoins(saldo, costo = 1) {
  if (!saldo || saldo < costo) {
    return {
      suficiente: false,
      mensaje: `Saldo insuficiente. Tienes ${saldo || 0} coins, necesitas ${costo}.`
    }
  }
  return { suficiente: true, mensaje: '' }
}

/**
 * Validación completa de un período laboral
 * @param {Object} periodo - Datos del período
 * @returns {Object} {valido, errores}
 */
export function validarPeriodo(periodo) {
  const errores = []
  
  if (!periodo) return { valido: false, errores: ['Período inválido'] }
  
  // Validar fechas
  const coherencia = validarCoherenciaTemporal(periodo.fechaDesde, periodo.fechaHasta)
  if (!coherencia.valido) {
    errores.push(coherencia.error)
  }
  
  // Validar aporte porcentaje
  if (periodo.aportePorcentaje) {
    const porcentaje = sanitizarMoneda(periodo.aportePorcentaje, 0, 100, 10)
    if (porcentaje < 0 || porcentaje > 100) {
      errores.push('Porcentaje de aporte debe estar entre 0 y 100')
    }
  }
  
  return {
    valido: errores.length === 0,
    errores
  }
}
